// Estado global de la PWA (store externo + useSyncExternalStore).
// - Arranca desde la caché local (funciona sin red con lo último visto).
// - Cada acción aplica un cambio optimista; si hay red se ejecuta y, si el servidor la rechaza,
//   se revierte. Sin red (o con cola pendiente) la acción se encola y se aplica al reconectar.

import { useSyncExternalStore } from 'react';
import type { Session } from '@supabase/supabase-js';
import { isClosedStatus, normalizeStatus, statusGroup, statusRank } from '@shared/domain/status.ts';
import { priorityRank } from '@shared/domain/priority.ts';
import { addDays, localDate, notionDateToIso } from '@shared/domain/time.ts';
import {
  formatAddedSeconds,
  formatHoursValue,
  hoursToSeconds,
  secondsToHours,
} from '@shared/domain/hours.ts';
import type { CardChanges } from '@shared/notion/properties.ts';
import type { Rounding } from '@shared/domain/rounding.ts';
import { api } from '../lib/api.ts';
import { cache, queueStorage } from '../lib/cache.ts';
import { ApiError, friendlyMessage, isNetworkError } from '../lib/errors.ts';
import { formatCalendarDate } from '../lib/format.ts';
import { ActionQueue, type NewAction, type QueuedAction } from '../lib/queue.ts';
import { supabase } from '../lib/supabase.ts';
import type {
  CardDetail,
  CardListItem,
  NotionUser,
  PersonSummary,
  Profile,
  SyncStatus,
  SyncTrigger,
  TimeEntry,
  WorkLog,
} from '../lib/types.ts';

export interface Toast {
  id: number;
  message: string;
  kind: 'info' | 'error';
  /** Botón del aviso (p. ej. «Corregir» tras detener el cronómetro). */
  action?: { label: string; run: () => void };
}

/** Hoja de horas abierta: iniciar/detener el cronómetro y registrar el total de un día. */
export interface HoursSheetState {
  cardId: string;
  /** true: además de registrar, ofrece iniciar o detener el cronómetro. */
  withTimer: boolean;
  /** Día a editar (por defecto hoy). */
  date?: string;
  /** Registro de otra persona (solo admin). */
  userId?: string;
}

export interface AppSnapshot {
  ready: boolean;
  userId: string | null;
  email: string | null;
  profile: Profile | null;
  cards: CardListItem[];
  notionUsers: NotionUser[];
  people: PersonSummary[];
  openEntry: TimeEntry | null;
  /** Registros propios de los últimos MY_LOGS_DAYS días. */
  myLogs: WorkLog[];
  syncStatus: SyncStatus | null;
  details: Record<string, CardDetail>;
  /** Registros (de todas las personas) de las tarjetas abiertas. */
  logsByCard: Record<string, WorkLog[]>;
  hoursSheet: HoursSheetState | null;
  online: boolean;
  syncing: boolean;
  pending: number;
  toasts: Toast[];
}

const MY_LOGS_DAYS = 92;

const initial: AppSnapshot = {
  ready: false,
  userId: null,
  email: null,
  profile: null,
  cards: [],
  notionUsers: [],
  people: [],
  openEntry: null,
  myLogs: [],
  syncStatus: null,
  details: {},
  logsByCard: {},
  hoursSheet: null,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  syncing: false,
  pending: 0,
  toasts: [],
};

function applyCardChanges<T extends CardListItem>(card: T, changes: CardChanges): T {
  const next = { ...card };
  if (changes.status !== undefined) {
    const status = normalizeStatus(changes.status);
    next.status = status;
    next.status_group = statusGroup(status);
    next.status_rank = statusRank(status);
    next.is_closed = isClosedStatus(status);
  }
  if (changes.miembros !== undefined) next.miembros = [...changes.miembros];
  if (changes.fechas !== undefined) {
    next.fechas = changes.fechas ? { ...changes.fechas, time_zone: null } : null;
    next.fechas_start = changes.fechas ? notionDateToIso(changes.fechas.start) : null;
    next.fechas_end = changes.fechas ? notionDateToIso(changes.fechas.end) : null;
  }
  if (changes.horas_estimadas !== undefined) next.horas_estimadas = changes.horas_estimadas;
  next.priority_rank = priorityRank(next.etiquetas);
  return next;
}

const sameLog = (a: Pick<WorkLog, 'user_id' | 'card_id' | 'work_date'>, b: typeof a) =>
  a.user_id === b.user_id && a.card_id === b.card_id && a.work_date === b.work_date;

const byDateDesc = (a: WorkLog, b: WorkLog) => b.work_date.localeCompare(a.work_date);

class AppStore {
  private state: AppSnapshot = initial;
  private listeners = new Set<() => void>();
  private persistScheduled = false;
  private toastSeq = 0;
  private started = false;
  readonly queue = new ActionQueue(queueStorage, (action) =>
    this.execute(action).then(() => undefined),
  );

  getState = (): AppSnapshot => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(patch: Partial<AppSnapshot>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
    this.schedulePersist();
  }

  /** Guarda en IndexedDB en el mismo tick (agrupando varios cambios seguidos). */
  private schedulePersist() {
    if (!this.state.userId || this.persistScheduled) return;
    this.persistScheduled = true;
    queueMicrotask(() => {
      this.persistScheduled = false;
      const s = this.state;
      if (!s.userId) return;
      void cache.saveState({
        userId: s.userId,
        profile: s.profile,
        cards: s.cards,
        notionUsers: s.notionUsers,
        people: s.people,
        openEntry: s.openEntry,
        myLogs: s.myLogs,
        syncStatus: s.syncStatus,
        savedAt: new Date().toISOString(),
      });
    });
  }

  toast(message: string, kind: Toast['kind'] = 'info', action?: Toast['action']) {
    const id = ++this.toastSeq;
    this.set({ toasts: [...this.state.toasts, { id, message, kind, action }] });
    setTimeout(() => this.dismissToast(id), kind === 'error' || action ? 7000 : 4000);
  }

  dismissToast(id: number) {
    this.set({ toasts: this.state.toasts.filter((t) => t.id !== id) });
  }

  // ---- Arranque y sesión -----------------------------------------------------------------

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    this.queue.subscribe((pending) => this.set({ pending }));
    window.addEventListener('online', () => {
      this.set({ online: true });
      void this.flushQueue();
    });
    window.addEventListener('offline', () => this.set({ online: false }));

    const cached = await cache.loadState();
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    if (cached && (!session || cached.userId === session.user.id)) {
      // Sin red y con el token vencido, se sigue mostrando la última copia del mismo usuario.
      if (session || !navigator.onLine) {
        this.set({
          userId: cached.userId,
          profile: cached.profile,
          cards: cached.cards,
          notionUsers: cached.notionUsers,
          people: cached.people,
          openEntry: cached.openEntry,
          myLogs: cached.myLogs ?? [],
          syncStatus: cached.syncStatus,
          email: cached.profile?.email ?? null,
        });
      }
    }
    if (session) this.set({ userId: session.user.id, email: session.user.email ?? null });
    this.set({ ready: true });

    supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'SIGNED_IN' && s && s.user.id !== this.state.userId) void this.onSignedIn(s);
      if (event === 'SIGNED_OUT') this.set({ ...initial, ready: true, online: navigator.onLine });
    });

    if (session && navigator.onLine) await this.onSignedIn(session);
  }

  private async onSignedIn(session: Session) {
    this.set({ userId: session.user.id, email: session.user.email ?? null });
    await this.flushQueue();
    await this.loadRemote();
    if (this.state.profile?.notion_user_id) void this.refresh('apertura');
  }

  /** Lee del servidor perfil, tarjetas, personas, cronómetro, registros propios y sincronización. */
  async loadRemote(): Promise<void> {
    const userId = this.state.userId;
    if (!userId || !navigator.onLine) return;
    try {
      let profile = await api.profile(userId);
      if (profile && !profile.notion_user_id) profile = await api.linkAuto();
      this.set({ profile });
      const notionUsers = await api.notionUsers();
      this.set({ notionUsers });
      if (!profile?.notion_user_id) return;
      const [people, cards, openEntry, myLogs, syncStatus] = await Promise.all([
        api.people(),
        api.cards(),
        api.openEntry(userId),
        api.myLogs(userId, addDays(localDate(new Date()), -MY_LOGS_DAYS)),
        api.syncStatus(),
      ]);
      this.set({ people, cards, openEntry, syncStatus });
      this.applyMyLogs(myLogs);
    } catch (e) {
      if (!isNetworkError(e)) this.toast(friendlyMessage(e), 'error');
    }
  }

  /** Sincronización incremental (apertura, botón o gesto) y recarga. */
  async refresh(trigger: SyncTrigger): Promise<void> {
    if (!navigator.onLine) {
      this.toast('Sin conexión: se muestra la última copia guardada.');
      return;
    }
    if (this.state.syncing) return;
    this.set({ syncing: true });
    try {
      await api.sync(trigger);
    } catch (e) {
      if (!isNetworkError(e))
        this.toast(`No se pudo sincronizar con Notion: ${friendlyMessage(e)}`, 'error');
    } finally {
      await this.loadRemote();
      this.set({ syncing: false });
    }
  }

  async signOut(): Promise<void> {
    await supabase.auth.signOut();
    await cache.clearAll();
    await this.queue.clear();
    this.set({ ...initial, ready: true, online: navigator.onLine });
  }

  setProfile(profile: Profile) {
    this.set({ profile });
    if (profile.notion_user_id) void this.refresh('apertura');
  }

  // ---- Detalle de tarjeta ----------------------------------------------------------------

  async openCard(id: string): Promise<void> {
    if (!this.state.details[id]) {
      const cached = await cache.loadCard(id);
      if (cached) {
        this.set({
          details: { ...this.state.details, [id]: cached.card },
          logsByCard: { ...this.state.logsByCard, [id]: cached.logs ?? [] },
        });
      }
    }
    if (!navigator.onLine) return;
    let card: CardDetail | null;
    try {
      const [fresh, logs] = await Promise.all([api.card(id), api.logsForCard(id)]);
      card = fresh;
      if (card) this.setCardData(id, card, logs);
    } catch (e) {
      if (!isNetworkError(e)) this.toast(friendlyMessage(e), 'error');
      return;
    }
    // Cuerpo perezoso: las corridas masivas no lo leen (falta, o quedó pendiente porque la
    // página cambió) y las imágenes alojadas en Notion vencen. Se lee con una corrida por página.
    const expired = card?.body_expires_at && Date.parse(card.body_expires_at) <= Date.now();
    if (!card || (card.body_synced_at && !expired)) return;
    try {
      await api.syncPage(id);
      const updated = await api.card(id);
      if (updated) this.setCardData(id, updated, this.state.logsByCard[id] ?? []);
    } catch (e) {
      if (!isNetworkError(e))
        this.toast(`No se pudo leer el contenido desde Notion: ${friendlyMessage(e)}`, 'error');
    }
  }

  private setCardData(id: string, card: CardDetail, logs: WorkLog[]) {
    this.set({
      details: { ...this.state.details, [id]: card },
      logsByCard: { ...this.state.logsByCard, [id]: logs },
    });
    void cache.saveCard(id, { card, logs });
  }

  private async reloadLogs(cardId: string) {
    if (!navigator.onLine) return;
    const logs = await api.logsForCard(cardId);
    const card = this.state.details[cardId];
    this.set({ logsByCard: { ...this.state.logsByCard, [cardId]: logs } });
    if (card) void cache.saveCard(cardId, { card, logs });
  }

  private async reloadMyLogs() {
    const userId = this.state.userId;
    if (!userId || !navigator.onLine) return;
    this.applyMyLogs(await api.myLogs(userId, addDays(localDate(new Date()), -MY_LOGS_DAYS)));
  }

  /** Guarda los registros propios y los refleja en las listas de las tarjetas abiertas. */
  private applyMyLogs(myLogs: WorkLog[]) {
    const userId = this.state.userId;
    const since = addDays(localDate(new Date()), -MY_LOGS_DAYS);
    const logsByCard = Object.fromEntries(
      Object.entries(this.state.logsByCard).map(([cardId, list]) => [
        cardId,
        [
          ...list.filter((l) => l.user_id !== userId || l.work_date < since),
          ...myLogs.filter((l) => l.card_id === cardId),
        ].sort(byDateDesc),
      ]),
    );
    this.set({ myLogs, logsByCard });
  }

  // ---- Registros por día: lectura y cambios locales ---------------------------------------

  /** Registro de (persona, tarjeta, día); por defecto, el propio. */
  logFor(cardId: string, date: string, userId = this.state.userId): WorkLog | undefined {
    const match = (l: WorkLog) =>
      l.card_id === cardId && l.work_date === date && l.user_id === userId && !l.deleted_at;
    return this.state.myLogs.find(match) ?? this.state.logsByCard[cardId]?.find(match);
  }

  private upsertLocal(log: WorkLog) {
    const mine = log.user_id === this.state.userId;
    const replace = (list: WorkLog[]) => [...list.filter((l) => !sameLog(l, log)), log];
    // Aunque la tarjeta no se haya abierto con conexión, su lista muestra el cambio pendiente.
    const cardList = this.state.logsByCard[log.card_id] ?? [];
    this.set({
      myLogs: mine ? replace(this.state.myLogs) : this.state.myLogs,
      logsByCard: { ...this.state.logsByCard, [log.card_id]: replace(cardList).sort(byDateDesc) },
    });
  }

  private removeLocal(key: Pick<WorkLog, 'user_id' | 'card_id' | 'work_date'>) {
    const cardList = this.state.logsByCard[key.card_id];
    this.set({
      myLogs: this.state.myLogs.filter((l) => !sameLog(l, key)),
      logsByCard: cardList
        ? { ...this.state.logsByCard, [key.card_id]: cardList.filter((l) => !sameLog(l, key)) }
        : this.state.logsByCard,
    });
  }

  private cardName(cardId: string): string {
    return (
      (this.state.cards.find((c) => c.id === cardId) ?? this.state.details[cardId])?.name ||
      'la tarjeta'
    );
  }

  /**
   * Lo que midió un cronómetro, sumado localmente al registro del día en que empezó (el
   * servidor hace lo mismo en stop_timer/start_timer) y avisado con «Corregir».
   */
  private addMeasured(entry: TimeEntry, endedAt: string, quiet: boolean) {
    const seconds = Math.round((Date.parse(endedAt) - Date.parse(entry.started_at)) / 1000);
    if (seconds < 60) {
      if (!quiet) this.toast('Menos de 1 minuto: no se sumó al registro.');
      return;
    }
    const userId = this.state.userId ?? '';
    const date = localDate(entry.started_at);
    const current = this.logFor(entry.card_id, date);
    const total = Math.min(86_400, (current?.seconds ?? 0) + seconds);
    this.upsertLocal({
      id: current?.id ?? `pendiente-${crypto.randomUUID()}`,
      user_id: userId,
      card_id: entry.card_id,
      work_date: date,
      seconds: total,
      needs_review: current?.needs_review ?? false,
      deleted_at: null,
      pending: true,
    });
    const when = date === localDate(new Date()) ? 'hoy llevas' : 'ese día llevas';
    this.toast(
      `Se sumaron ${formatAddedSeconds(seconds)} a «${this.cardName(entry.card_id)}» · ${when} ${formatHoursValue(secondsToHours(total))}`,
      'info',
      {
        label: 'Corregir',
        run: () => this.openHoursSheet({ cardId: entry.card_id, withTimer: false, date }),
      },
    );
  }

  // ---- Hoja de horas ---------------------------------------------------------------------

  openHoursSheet(sheet: HoursSheetState) {
    this.set({ hoursSheet: sheet });
  }

  closeHoursSheet() {
    this.set({ hoursSheet: null });
  }

  // ---- Acciones con optimismo, reversión y cola offline ---------------------------------

  private async execute(action: QueuedAction): Promise<unknown> {
    switch (action.kind) {
      case 'update-card':
        return api.updateCard(action.card_id, action.changes);
      case 'start-timer':
        return api.startTimer(action.card_id, action.at);
      case 'stop-timer':
        return api.stopTimer(action.at);
      case 'set-work-log':
        return api.setWorkLog(action.log);
      case 'delete-work-log':
        return api.deleteWorkLog(action.log);
      case 'set-pref': {
        const userId = this.state.userId;
        if (!userId) throw new Error('Sin sesión.');
        return api.updateProfile(userId, action.patch);
      }
      default:
        // Acciones de la v1 (carga manual con inicio y fin) que quedaron en la cola guardada.
        throw new ApiError(
          'Un registro de horas hecho sin conexión con la versión anterior de Tablero no se pudo aplicar; regístralo de nuevo.',
          false,
        );
    }
  }

  private snapshot() {
    const { cards, details, openEntry, logsByCard, myLogs, profile } = this.state;
    return () => this.set({ cards, details, openEntry, logsByCard, myLogs, profile });
  }

  private async perform(action: NewAction, optimistic: () => void): Promise<boolean> {
    const revert = this.snapshot();
    const at = action.at ?? new Date().toISOString();
    optimistic();
    if (!navigator.onLine || this.queue.pending > 0) {
      await this.queue.enqueue({ ...action, at } as NewAction);
      if (navigator.onLine) void this.flushQueue();
      return true;
    }
    try {
      const result = await this.execute({ ...action, at, qid: 'directo' } as QueuedAction);
      await this.afterApplied({ ...action, at, qid: 'directo' } as QueuedAction, result);
      return true;
    } catch (e) {
      if (isNetworkError(e)) {
        await this.queue.enqueue({ ...action, at } as NewAction);
        return true;
      }
      revert();
      this.toast(friendlyMessage(e), 'error');
      return false;
    }
  }

  private async afterApplied(action: QueuedAction, result: unknown) {
    switch (action.kind) {
      case 'update-card': {
        const row = result as CardListItem;
        this.set({
          cards: this.state.cards.map((c) => (c.id === row.id ? { ...c, ...row } : c)),
          details: this.state.details[row.id]
            ? {
                ...this.state.details,
                [row.id]: { ...this.state.details[row.id], ...row } as CardDetail,
              }
            : this.state.details,
        });
        break;
      }
      case 'start-timer':
      case 'stop-timer': {
        // El servidor sumó lo medido a work_logs: se relee lo propio (y las tarjetas abiertas).
        const userId = this.state.userId;
        if (userId) this.set({ openEntry: await api.openEntry(userId) });
        await this.reloadMyLogs();
        break;
      }
      case 'set-work-log':
        this.upsertLocal(result as WorkLog);
        break;
      case 'delete-work-log':
      case 'set-pref':
        break;
    }
  }

  async flushQueue(): Promise<void> {
    if (!navigator.onLine || this.queue.pending === 0) return;
    const result = await this.queue.flush();
    for (const r of result.rejected) {
      this.toast(
        `No se pudo aplicar un cambio hecho sin conexión: ${friendlyMessage(r.error)}`,
        'error',
      );
    }
    if (result.applied.length > 0 || result.rejected.length > 0) {
      await this.loadRemote();
      for (const id of Object.keys(this.state.logsByCard)) await this.reloadLogs(id);
      if (result.applied.length > 0 && result.remaining === 0) {
        this.toast('Cambios hechos sin conexión aplicados.');
      }
    }
  }

  updateCard(cardId: string, changes: CardChanges): Promise<boolean> {
    return this.perform({ kind: 'update-card', card_id: cardId, changes }, () => {
      this.set({
        cards: this.state.cards.map((c) => (c.id === cardId ? applyCardChanges(c, changes) : c)),
        details: this.state.details[cardId]
          ? {
              ...this.state.details,
              [cardId]: applyCardChanges(this.state.details[cardId] as CardDetail, changes),
            }
          : this.state.details,
      });
    });
  }

  setSoloLoMio(value: boolean): Promise<boolean> {
    return this.setPref({ solo_lo_mio: value });
  }

  setRedondeo(value: Rounding): Promise<boolean> {
    return this.setPref({ redondeo: value });
  }

  private setPref(patch: Partial<Pick<Profile, 'solo_lo_mio' | 'redondeo'>>): Promise<boolean> {
    return this.perform({ kind: 'set-pref', patch }, () => {
      if (this.state.profile) this.set({ profile: { ...this.state.profile, ...patch } });
    });
  }

  startTimer(card: CardListItem): Promise<boolean> {
    if (card.is_closed) {
      this.toast('El cronómetro no está disponible en tarjetas cerradas.', 'error');
      return Promise.resolve(false);
    }
    const at = new Date().toISOString();
    return this.perform({ kind: 'start-timer', card_id: card.id, at }, () => {
      const previous = this.state.openEntry;
      if (previous?.card_id === card.id) return;
      if (previous) this.addMeasured(previous, at, true);
      this.set({
        openEntry: {
          id: `pendiente-${crypto.randomUUID()}`,
          user_id: this.state.userId ?? '',
          card_id: card.id,
          started_at: at,
          ended_at: null,
          source: 'cronometro',
          note: null,
          needs_review: false,
          warned_at: null,
          deleted_at: null,
          pending: true,
        },
      });
    });
  }

  stopTimer(): Promise<boolean> {
    const at = new Date().toISOString();
    return this.perform({ kind: 'stop-timer', at }, () => {
      const open = this.state.openEntry;
      if (open) this.addMeasured(open, at, false);
      this.set({ openEntry: null });
    });
  }

  /** Fija el total de (persona, tarjeta, día). Mínimo 0,5 h (lo valida también la base). */
  async setWorkLog(input: {
    cardId: string;
    date: string;
    hours: number;
    userId?: string;
  }): Promise<boolean> {
    const me = this.state.userId ?? '';
    const userId = input.userId ?? me;
    const seconds = hoursToSeconds(input.hours);
    const current = this.logFor(input.cardId, input.date, userId);
    const ok = await this.perform(
      {
        kind: 'set-work-log',
        log: {
          card_id: input.cardId,
          work_date: input.date,
          seconds,
          ...(userId !== me ? { user_id: userId } : {}),
        },
      },
      () =>
        this.upsertLocal({
          id: current?.id ?? `pendiente-${crypto.randomUUID()}`,
          user_id: userId,
          card_id: input.cardId,
          work_date: input.date,
          seconds,
          needs_review: false,
          deleted_at: null,
          pending: true,
        }),
    );
    if (ok) {
      const when =
        input.date === localDate(new Date()) ? '' : ` el ${formatCalendarDate(input.date)}`;
      this.toast(
        `Registrado: ${formatHoursValue(input.hours)}${when} en «${this.cardName(input.cardId)}».`,
      );
    }
    return ok;
  }

  deleteWorkLog(log: WorkLog): Promise<boolean> {
    const me = this.state.userId ?? '';
    return this.perform(
      {
        kind: 'delete-work-log',
        log: {
          card_id: log.card_id,
          work_date: log.work_date,
          ...(log.user_id !== me ? { user_id: log.user_id } : {}),
        },
      },
      () => this.removeLocal(log),
    );
  }
}

export const store = new AppStore();

export function useApp(): AppSnapshot {
  return useSyncExternalStore(store.subscribe, store.getState);
}
